// A lazy route chunk or its CSS sometimes fails to load in the guest's
// browser, and the guest lands on the error page. SigNoz showed ~24 of these a
// day, 85% from iPhones, spread evenly and not tied to deploys, and the files
// that failed were current and still in S3 (samera/samera#3594). A reload
// fetches them again, so on the first failure we reload the page once.
//
// Vite dispatches `vite:preloadError` for a failed CSS preload and for a
// failed `import()` of the chunk itself. We do not call preventDefault(): the
// import would then resolve to undefined and the router would throw a new,
// less useful error. The original error is thrown as before, and the page
// reloads.
//
// The reload waits one second. The failures look like the dropped Safari
// connections behind samera/samera#3593, where a retry at once landed in the
// same network blip and a retry after TanStack's 1 s delay recovered.
//
// The loop guard stores the build that already reloaded in sessionStorage, so
// a tab reloads at most once per build. A second failure in the same build is
// reported as before. Without sessionStorage there is no guard, so no reload.
import {
  flushLogger,
  logWarning,
  suppressReports,
} from '~shared/observability/observability';

const STORAGE_KEY = 'samera-one:chunk-load-reload';
const FLUSH_TIMEOUT_MS = 200;
export const RELOAD_DELAY_MS = 1000;

type Dependencies = {
  reload: () => void;
  storage: () => Storage;
  // import.meta.url of this module. It is bundled into the hashed entry chunk,
  // so it changes with every build.
  buildId: string;
};

const defaults: Dependencies = {
  reload: () => window.location.reload(),
  storage: () => window.sessionStorage,
  buildId: import.meta.url,
};

// Records the reload before it happens. False when this build has already
// reloaded or the storage is unusable.
const claimReload = (storage: () => Storage, buildId: string): boolean => {
  try {
    const store = storage();
    if (store.getItem(STORAGE_KEY) === buildId) return false;
    store.setItem(STORAGE_KEY, buildId);
    return true;
  } catch {
    return false;
  }
};

// The WARN replaces the ERROR that suppressReports() drops, so it keeps what
// that ERROR would have carried. A chunk that loaded but threw while it
// evaluated also arrives here, and only its type and stack show that.
const describePayload = (payload: unknown): Record<string, unknown> =>
  payload instanceof Error
    ? {
        'chunk.error': payload.message,
        'chunk.error_type': payload.name,
        'chunk.error_stack': payload.stack,
      }
    : { 'chunk.error': String(payload) };

export const installChunkLoadReload = (
  overrides: Partial<Dependencies> = {},
): (() => void) => {
  const { reload, storage, buildId } = { ...defaults, ...overrides };
  let reloading = false;

  const onPreloadError = (event: Event): void => {
    if (reloading) return;
    if (!claimReload(storage, buildId)) return;
    reloading = true;

    logWarning(new Error('Chunk load failed, reloading'), {
      kind: 'chunk-load-reload',
      ...describePayload((event as Event & { payload?: unknown }).payload),
    });
    suppressReports();
    // The flush is bounded well below the delay, so the delay decides when.
    // The reload takes whatever URL is current then: on an in-app navigation
    // the router commits the new URL after this event, and that URL is the
    // page the guest asked for.
    void Promise.all([
      flushLogger(FLUSH_TIMEOUT_MS),
      new Promise(resolve => setTimeout(resolve, RELOAD_DELAY_MS)),
    ]).then(() => reload());
  };

  window.addEventListener('vite:preloadError', onPreloadError);
  return () => window.removeEventListener('vite:preloadError', onPreloadError);
};

// Importing the module installs the handler, like dom-translation-guard.ts. It
// must run before the router loads its first lazy chunk.
if (typeof window !== 'undefined') installChunkLoadReload();
